import { useAppSelector } from '../store/store';

/**
 * Custom hook to get user display data from Redux
 * Provides consistent user information across the app
 */
export const useUserDisplay = () => {
  const { user } = useAppSelector(state => state.auth);
  const { data: profile } = useAppSelector(state => state.profile);

  // Get profile photo with fallback
  const getProfilePhoto = () => {
    // Try primary photo from photos array
    if (profile?.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
      const primaryPhoto = profile.photos.find((photo: any) => photo.isPrimary);
      if (primaryPhoto?.url) return primaryPhoto.url;
      
      // Fallback to first photo
      if (profile.photos[0]?.url) return profile.photos[0].url;
    }
    
    // Fallback to profilePhoto field
    if (profile?.profilePhoto) return profile.profilePhoto;
    
    // No photo available
    return null;
  };

  // Get all profile photos
  const getProfilePhotos = () => {
    if (profile?.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
      return profile.photos.map((photo: any) => photo.url || photo).filter(Boolean);
    }
    
    if (profile?.profilePhoto) {
      return [profile.profilePhoto];
    }
    
    return [];
  };

  // Get user's full name
  const getFullName = () => {
    if (!user) return 'User';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
  };

  // Get user's first name
  const getFirstName = () => {
    return user?.firstName || 'User';
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (!user) return 'U';
    const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial || 'U';
  };

  // Get location string
  const getLocation = () => {
    if (!profile) return 'India';
    
    const parts = [
      profile.city,
      profile.state,
      profile.country
    ].filter(Boolean);
    
    return parts.join(', ') || 'India';
  };

  // Get age from date of birth
  const getAge = () => {
    if (!profile?.dateOfBirth) return null;
    
    const birthDate = new Date(profile.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Get height in feet'inches format
  const getHeight = () => {
    if (!profile?.height) return null;
    
    const totalInches = profile.height;
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    
    return `${feet}'${inches}"`;
  };

  // Get formatted education
  const getEducation = () => {
    if (!profile?.education) return null;
    
    const { level, field, institution } = profile.education;
    
    if (field && institution) {
      return `${field} at ${institution}`;
    } else if (field) {
      return field;
    } else if (level) {
      return level;
    }
    
    return null;
  };

  // Get occupation
  const getOccupation = () => {
    return profile?.occupation || null;
  };

  // Get bio/about
  const getBio = () => {
    return profile?.about || profile?.bio || null;
  };

  // Get religion
  const getReligion = () => {
    return profile?.religion || null;
  };

  // Get marital status (normalized to display format)
  const getMaritalStatus = () => {
    if (!profile?.maritalStatus) return null;
    
    // Convert from database format (e.g., "never_married") to display format ("Never Married")
    return profile.maritalStatus
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Check if profile is complete
  const isProfileComplete = () => {
    if (!profile) return false;
    
    const requiredFields = [
      profile.gender,
      profile.dateOfBirth,
      profile.maritalStatus,
      profile.religion,
      profile.city,
      profile.state,
      profile.country,
      profile.education?.level,
      profile.occupation
    ];
    
    return requiredFields.every(field => field && field !== '');
  };

  // Calculate profile completion percentage
  const getProfileCompletionPercentage = () => {
    if (!profile) return 0;
    
    let completed = 0;
    const fields = [
      profile.about || profile.bio,
      profile.city,
      profile.state,
      profile.education?.field,
      profile.education?.institution,
      profile.occupation,
      profile.annualIncome,
      profile.profilePhoto || (profile.photos && profile.photos.length > 0),
      profile.height,
      profile.religion,
      profile.community,
      profile.maritalStatus
    ];
    
    fields.forEach(field => {
      if (field && field !== '' && field !== 0) completed++;
    });
    
    return Math.round((completed / fields.length) * 100);
  };

  return {
    user,
    profile,
    getProfilePhoto,
    getProfilePhotos,
    getFullName,
    getFirstName,
    getInitials,
    getLocation,
    getAge,
    getHeight,
    getEducation,
    getOccupation,
    getBio,
    getReligion,
    getMaritalStatus,
    isProfileComplete,
    getProfileCompletionPercentage,
  };
};